var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Factory, Tween, ViewerModule } from '@g.frame/core';
import { AUDIO, FONT, Loader } from '@g.frame/common.loaders';
import { Euler, HemisphereLight, Quaternion, Vector3 } from 'three';
import { DesktopModule } from '@g.frame/desktop';
import { ActionController } from '@g.frame/common.action_controller';
import { SessionController } from '@verybigthings/cerify-ed-simulation--backend-connect';
import { OculusQuestModule } from '@g.frame/oculus.quest';
import { XREvent } from '@g.frame/common.xr_manager';
import Lobby from './Lobby/Lobby';
import TractorLevel from './TractorLevel/TractorLevel';
import UITestLevel from './UITestLevel/UITestLevel';
import UIManager from "../g.frame.modules/uiModule/src/UIManager";
import Environment from '../modules/environment/Environment';
import LeavesLevel from "./LeavesLevel/LeavesLevel";
import InjectionLevel from "./InjectionLevel/InjectionLevel";
import VRCover from '../utils/vrCover';
SessionController.salt = SECRET_SALT;
export default class LevelsManager extends ViewerModule {
    constructor(modulesProcessor, preloader) {
        var _a;
        super();
        this.modulesProcessor = modulesProcessor;
        this.preloader = preloader;
        this.activeLevel = null;
        this.isVR = false;
        this.simStarted = false;
        this.goToVRCallback = () => {
        };
        this.goFromVRCallback = () => {
        };
        this.loader = this.modulesProcessor.agents.get(Loader);
        this.actionController = this.modulesProcessor.agents.get(ActionController);
        this.uiManager = this.modulesProcessor.agents.get(Factory).getFactory(UIManager)('');
        this.environment = new Environment(this.modulesProcessor);
        this.loader.addResources([
            // FONT AWESOME
            {
                name: 'FontAwesome&woff2-900-normal',
                url: require('@verybigthings/fontawesome-pro/webfonts/fa-solid-900.woff2'),
                type: FONT
            },
            {
                name: 'FontAwesome&woff2-400-normal',
                url: require('@verybigthings/fontawesome-pro/webfonts/fa-regular-400.woff2'),
                type: FONT
            },
            {
                name: 'AUDIO_music',
                url: require('../../assets/audio/music.mp3'),
                type: AUDIO
            },
        ]);
        this.sideLevels = {
            '#TestLevel': new UITestLevel(this.modulesProcessor, this.uiManager, this.environment),
        };
        this.levelList = {
            '#Lobby': new Lobby(this.modulesProcessor, this.uiManager, this.environment),
            '#TractorLevel': new TractorLevel(this.modulesProcessor, this.uiManager, this.environment, this),
            '#LeavesLevel': new LeavesLevel(this.modulesProcessor, this.uiManager, this.environment, this),
            '#InjectionLevel': new InjectionLevel(this.modulesProcessor, this.uiManager, this.environment, this),
        };
        this.isVR = !!((_a = modulesProcessor.modulesInstances.get(OculusQuestModule)) === null || _a === void 0 ? void 0 : _a.length);
    }
    init() {
        if (this.isVR) {
            this.vrCover = new VRCover;
            this.vrCover.show();
            const manager = this.modulesProcessor.modulesInstances.get(OculusQuestModule)[0];
            manager.on(XREvent.goToVR, () => { this.vrCover.hide(); });
            manager.on(XREvent.goFromVR, () => { this.vrCover.show(); });
        }
        const cameraPosition = new Vector3(8.088, 2, 10.604);
        const isDebug = false;
        this.controls = this.modulesProcessor.modulesInstances.get(DesktopModule)[1];
        this.controls.target.copy(cameraPosition);
        if (this.isVR) {
            this.modulesProcessor.viewer.cameraWrapParent.position.copy(cameraPosition);
            this.modulesProcessor.viewer.cameraWrapParent.position.y = 0;
        }
        else {
            this.controls.object.position.copy(cameraPosition.add(new Vector3(0.01, 0, 0)));
        }
        this.controls.rotateSpeed = isDebug ? 1 : -0.15;
        this.controls.enableZoom = isDebug;
        this.controls.enablePan = isDebug;
        this.controls.update();
        this.environment.init();
        this.initLights();
        this.addObject(this.environment);
        this.uiManager.init(this.modulesProcessor);
        for (let key in this.levelList) {
            this.levelList[key].init();
        }
        //
        window['modules_processor'] = this.modulesProcessor;
        window['controls_'] = this.controls;
        //debug
        this.musicInit();
        this.prepareMenu();
        this.startLevel(location.hash);
        this.activeLevel.once('start_level', () => {
            this.simStarted = true;
            this.mainTheme.play();
        });
    }
    prepareMenu() {
        this.uiManager.on('exit_to_lobby', () => {
            this.uiManager.hideText();
            this.uiManager.hidePopUpScreen();
            this.activeLevel && this.activeLevel.resetStage();
            this.startLevel('#Lobby');
        });
        this.uiManager.on('music_toggle', (data) => {
            const muted = data.data.muted;
            if (muted) {
                this.mainTheme.volume = 0.0;
            }
            else {
                this.mainTheme.volume = 0.35;
            }
        });
    }
    startLevel(hash) {
        if (this.mainTheme && this.simStarted)
            this.mainTheme.play();
        if (this.activeLevel)
            this.removeObject(this.activeLevel);
        this.activeLevel = this.getLevel(hash);
        location.href = (this.sideLevels[hash] || this.levelList[hash]) ? hash : '#Lobby';
        if (location.hash === '#Lobby') {
            this.uiManager.toggleExitButton(true);
            if (this.getLevelsCompletion()) {
                this.sendResults();
            }
        }
        else {
            this.uiManager.toggleExitButton(false);
        }
        this.levelFlyTo(this.activeLevel, false, () => {
            this.addObject(this.activeLevel);
            this.activeLevel.startLevel();
            this.activeLevel.once('next_level', (event) => {
                this.startLevel(event.data.level);
            });
        });
    }
    levelFlyTo(stage, toActivated = true, onCompleteCallback) {
        var _a;
        const startCameraPosition = this.controls.object.position.clone();
        const startTargetPosition = this.controls.target.clone();
        let endCameraPosition = stage.controlsPosition.camera.clone();
        const endTargetPosition = stage.controlsPosition.target.clone();
        if (this.isVR) {
            endCameraPosition = ((_a = stage.controlsPosition.cameraVR) === null || _a === void 0 ? void 0 : _a.clone()) || endCameraPosition;
            endCameraPosition.y = 0;
            endTargetPosition.y = 0;
            this.modulesProcessor.viewer.cameraWrapParent.position.copy(endCameraPosition);
            const direction = endTargetPosition.sub(endCameraPosition).normalize();
            const rotation = new Quaternion().setFromUnitVectors(new Vector3(0, 0, -1), direction);
            const euler = new Euler().setFromQuaternion(rotation);
            this.modulesProcessor.viewer.camera.parent.rotation.set(0, euler.y, 0);
            if (toActivated)
                stage.beforeEnable();
            setTimeout(() => onCompleteCallback && onCompleteCallback(), 900);
        }
        else {
            new Tween({ alpha: 0 }).to({ alpha: 1 }, 900)
                .onStart(() => {
                if (toActivated)
                    stage.beforeEnable();
            })
                .onUpdate((alpha) => {
                const newCameraPosition = startCameraPosition.clone().lerp(endCameraPosition.clone(), alpha);
                this.controls.object.position.copy(newCameraPosition);
                const newTargetPosition = startTargetPosition.clone().lerp(endTargetPosition.clone(), alpha);
                this.controls.target.copy(newTargetPosition);
                this.controls.update();
            })
                .onComplete(() => {
                onCompleteCallback && onCompleteCallback();
            })
                .start();
        }
    }
    getLevelsCompletion() {
        let completed = true;
        for (let level in this.levelList) {
            completed = (this.levelList[level].completed === false) ? false : completed;
        }
        return completed;
    }
    getLevel(hash) {
        return this.sideLevels[hash] || this.levelList[hash] || this.levelList['#Lobby'];
    }
    initLights() {
        const light = new HemisphereLight(0xeeeeee, 0x333333, 1);
        this.addObject(light);
    }
    sendResults() {
        return __awaiter(this, void 0, void 0, function* () {
            yield SessionController.init(null, null, 'Agriculture', null, 1);
            const maxScore = 100;
            const score = 100;
            const payload = {
                'max_score': maxScore,
                'score': score
            };
            yield SessionController.updateSessionEntries(payload, score, maxScore);
            yield SessionController.endSession('items');
        });
    }
    musicInit() {
        this.mainTheme = this.loader.getResource('AUDIO_music');
        this.mainTheme.volume = 0.35;
        this.mainTheme.loop = true;
    }
}
